// Mock data for Ransen.io - AI Growth Agency

export const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Industries', path: '/industries' },
  { label: 'Locations', path: '/locations' },
  { label: 'Case Studies', path: '/case-studies' },
  { label: 'Resources', path: '/resources' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export const HERO_STATS = [
  { value: '500+', label: 'Projects Delivered' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '150+', label: 'Happy Clients' },
  { value: '320%', label: 'Average ROI' },
];

export const SERVICES = [
  {
    slug: 'paid-media-advertising',
    icon: 'Target',
    title: 'Paid Media Advertising',
    short: 'Full-funnel paid media strategies that guide prospects from awareness to conversion.',
    long: 'We manage campaigns across Google, Meta, LinkedIn, Programmatic, CTV, and 15+ advertising platforms to deliver predictable, scalable growth. Full-funnel omnichannel campaign architecture with AI-powered bid management, dynamic creative optimization, and multi-touch attribution.',
    outcomes: ['320% average ROI', 'Cross-platform budget optimization', 'AI-powered bid management', 'Multi-touch attribution']
  },
  {
    slug: 'social-media-management',
    icon: 'Share2',
    title: 'Social Media Management',
    short: 'End-to-end social media strategy, content creation, and community management focused on brand growth.',
    long: 'End-to-end social media strategy, content creation, and community management focused on building brand presence, engagement, and long-term audience growth across Meta, TikTok, LinkedIn, and Instagram.',
    outcomes: ['Content velocity at scale', 'Community management', 'Cross-platform strategy', 'Engagement optimization']
  },
  {
    slug: 'website-design-development',
    icon: 'Monitor',
    title: 'Website Design & Development',
    short: 'Custom-designed, conversion-focused websites built for performance, scalability, and user experience.',
    long: 'Custom-designed, conversion-focused websites built for performance, scalability, and user experience — optimized to support marketing, lead generation, and business growth.',
    outcomes: ['Conversion-first design', 'Core Web Vitals optimized', 'CMS + headless options', 'Global CDN performance']
  },
  {
    slug: 'search-engine-optimization',
    icon: 'Search',
    title: 'Search Engine Optimization (SEO)',
    short: 'Comprehensive SEO strategies covering technical, content, keywords, and authority building.',
    long: 'Comprehensive SEO strategies covering technical optimization, content, keywords, and authority building to improve organic visibility, rankings, and qualified traffic.',
    outcomes: ['Technical foundations', 'Programmatic pages', 'Entity + topical authority', 'Multi-region SEO']
  },
  {
    slug: 'video-production',
    icon: 'Video',
    title: 'Video Production',
    short: 'High-quality video and visual content that tells your brand story and drives engagement.',
    long: 'High-quality video and visual content that tells your brand story, showcases products or services, and drives engagement across digital platforms — from short-form social to full brand films.',
    outcomes: ['Brand films & storytelling', 'Performance creative', 'Short-form social', 'Product demos']
  },
  {
    slug: 'ai-automation',
    icon: 'Bot',
    title: 'AI Automation',
    short: 'Custom AI-powered automations for marketing, operations, customer engagement, and analytics.',
    long: 'Custom AI-powered automations for marketing, operations, customer engagement, and analytics — helping businesses save time, reduce costs, and scale efficiently with production-grade AI agents.',
    outcomes: ['Custom AI agents', 'Ops automation', 'Content pipelines', 'Reporting copilots']
  },
  {
    slug: 'business-operations-support',
    icon: 'Headphones',
    title: 'Business Operations Support',
    short: 'Dedicated support for inbound inquiries, lead follow-up, appointment booking, and customer care.',
    long: 'Dedicated support for inbound inquiries, lead follow-up, appointment booking, and customer care, ensuring fast response times and a seamless customer experience across time zones.',
    outcomes: ['24/7 coverage', 'Lead follow-up', 'Appointment booking', 'Customer care']
  },
  {
    slug: 'llmo-optimization',
    icon: 'Sparkles',
    title: 'LLMO (Large Language Model Optimization)',
    short: 'Optimize your content and digital presence for AI-powered search engines like ChatGPT, Claude, and Gemini.',
    long: 'Optimize your content and digital presence for AI-powered search engines and language models like ChatGPT, Claude, and Gemini to increase brand visibility in AI-generated responses — the new discovery layer.',
    outcomes: ['AI citation tracking', 'Knowledge graph work', 'Answer-engine content', 'Entity-based optimization']
  },
  {
    slug: 'aeo-optimization',
    icon: 'MessageSquare',
    title: 'AEO (Answer Engine Optimization)',
    short: 'Optimize for answer engines and featured snippets to capture position zero in search results.',
    long: 'Optimize for answer engines and featured snippets to capture position zero in search results, voice search queries, and AI-powered answer platforms for maximum visibility.',
    outcomes: ['Featured snippet capture', 'Voice search optimization', 'Structured data implementation', 'Answer-first content']
  },
];

export const WHY_POINTS = [
  { icon: 'Rocket', title: 'Performance-first strategy', desc: 'Every dollar tied to a hypothesis, forecast, and result.' },
  { icon: 'Bot', title: 'AI & automation-led execution', desc: 'We deploy LLMs and agents where humans slow us down.' },
  { icon: 'Layers', title: 'Full-funnel paid media', desc: 'Search, social, video, programmatic — orchestrated as one.' },
  { icon: 'Activity', title: 'Advanced tracking & analytics', desc: 'Server-side, warehouse-native, consent-compliant.' },
  { icon: 'Search', title: 'SEO & LLM visibility', desc: 'Rank in Google and get cited by AI assistants.' },
  { icon: 'Globe2', title: 'Scalable growth systems', desc: 'Localized, compliant, and built to compound.' },
];

// 12 industries — exact match to ransen.ca
export const INDUSTRIES = [
  { slug: 'ecommerce-retail', title: 'E-Commerce & Retail', icon: 'ShoppingBag', blurb: 'Driving online sales with shopping campaigns, dynamic product ads, and full-funnel retargeting that maximizes ROAS.' },
  { slug: 'saas-technology', title: 'SaaS & Technology', icon: 'Laptop', blurb: 'Pipeline generation for PLG, freemium, and enterprise SaaS motions tied to ARR.' },
  { slug: 'healthcare-wellness', title: 'Healthcare & Wellness', icon: 'Heart', blurb: 'Patient acquisition with compliance-first advertising across search, social, and programmatic.' },
  { slug: 'professional-services', title: 'Professional Services', icon: 'Briefcase', blurb: 'High-trust growth for consulting, accounting, and specialized service firms.' },
  { slug: 'real-estate', title: 'Real Estate', icon: 'Home', blurb: 'Lead generation for brokers, developers, and multi-market home builders.' },
  { slug: 'finance-insurance', title: 'Finance & Insurance', icon: 'TrendingUp', blurb: 'Regulated-industry growth programs with rigorous compliance and CRO-first pages.' },
  { slug: 'education-elearning', title: 'Education & E-Learning', icon: 'BookOpen', blurb: 'Enrollment growth for online academies, universities, and edtech platforms.' },
  { slug: 'hospitality-travel', title: 'Hospitality & Travel', icon: 'Plane', blurb: 'Bookings and brand growth for hotels, resorts, tour operators, and DMOs.' },
  { slug: 'manufacturing-b2b', title: 'Manufacturing & B2B', icon: 'Factory', blurb: 'Long-cycle B2B demand generation, ABM, and multi-stakeholder pipelines.' },
  { slug: 'legal-services', title: 'Legal Services', icon: 'Scale', blurb: 'High-value case leads across search, LSA, and targeted display.' },
  { slug: 'automotive', title: 'Automotive', icon: 'Car', blurb: 'Dealer group and aftermarket growth with local + national blended media.' },
  { slug: 'food-beverage', title: 'Food & Beverage', icon: 'UtensilsCrossed', blurb: 'DTC beverages, CPG brands, and restaurant groups scaling with paid + creative.' },
];

// Countries we serve
export const MARKETS = [
  { slug: 'canada', name: 'Canada', flag: '🇨🇦', region: 'North America', tag: 'Toronto · Vancouver · Montreal' },
  { slug: 'united-states', name: 'United States', flag: '🇺🇸', region: 'North America', tag: 'NYC · LA · Miami · Austin' },
  { slug: 'united-kingdom', name: 'United Kingdom', flag: '🇬🇧', region: 'Europe', tag: 'London · Manchester' },
  { slug: 'germany', name: 'Germany', flag: '🇩🇪', region: 'Europe', tag: 'Berlin · Munich · Frankfurt' },
  { slug: 'france', name: 'France', flag: '🇫🇷', region: 'Europe', tag: 'Paris · Lyon' },
  { slug: 'netherlands', name: 'Netherlands', flag: '🇳🇱', region: 'Europe', tag: 'Amsterdam · Rotterdam' },
  { slug: 'spain', name: 'Spain', flag: '🇪🇸', region: 'Europe', tag: 'Madrid · Barcelona' },
  { slug: 'australia', name: 'Australia', flag: '🇦🇺', region: 'APAC', tag: 'Sydney · Melbourne · Brisbane' },
  { slug: 'new-zealand', name: 'New Zealand', flag: '🇳🇿', region: 'APAC', tag: 'Auckland · Wellington' },
  { slug: 'uae', name: 'UAE', flag: '🇦🇪', region: 'Middle East', tag: 'Dubai · Abu Dhabi' },
  { slug: 'saudi-arabia', name: 'Saudi Arabia', flag: '🇸🇦', region: 'Middle East', tag: 'Riyadh · Jeddah' },
  { slug: 'qatar', name: 'Qatar', flag: '🇶🇦', region: 'Middle East', tag: 'Doha' },
  { slug: 'singapore', name: 'Singapore', flag: '🇸🇬', region: 'APAC', tag: 'APAC hub' },
  { slug: 'malaysia', name: 'Malaysia', flag: '🇲🇾', region: 'APAC', tag: 'Kuala Lumpur' },
  { slug: 'india', name: 'India', flag: '🇮🇳', region: 'APAC', tag: 'Mumbai · Bangalore · Delhi' },
];

export const PROCESS_STEPS = [
  { step: '01', title: 'Discovery', desc: 'We analyze your business, competitors, and target audience to identify growth opportunities.' },
  { step: '02', title: 'Strategy', desc: 'Custom marketing plans designed to maximize ROI and achieve your specific business goals.' },
  { step: '03', title: 'Execute', desc: 'Our team implements campaigns across all channels with precision and creativity.' },
  { step: '04', title: 'Optimize', desc: 'Continuous monitoring and data-driven optimization to improve performance.' },
];

export const CASE_STUDIES = [
  {
    slug: 'ecommerce-revenue-growth',
    industry: 'Ecommerce',
    title: 'Ecommerce Revenue Growth',
    metric: '4.6x ROAS with CAPI + creative velocity',
    summary: 'Deployed server-side tracking, CAPI, and a creative testing engine to scale a DTC brand from $1.2M to $9.8M in annual paid revenue.',
    tags: ['Meta Ads', 'CAPI', 'Creative'],
    image: 'https://images.unsplash.com/photo-1522204523234-8729aa6e3d5f?w=1200&q=80'
  },
  {
    slug: 'saas-demand-generation',
    industry: 'SaaS · B2B',
    title: 'SaaS Demand Generation',
    metric: '68% lower CAC, 2.4x SQL rate',
    summary: 'Full-funnel LinkedIn + Google Ads with ABM overlays, offline conversion imports, and a rebuilt lifecycle model tied to CRM revenue.',
    tags: ['LinkedIn', 'ABM', 'Analytics'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80'
  },
  {
    slug: 'real-estate-lead-generation',
    industry: 'Real Estate',
    title: 'Real Estate Lead Generation',
    metric: '312% increase in qualified leads',
    summary: 'For a multi-market home builder, we rebuilt tracking, launched geo-targeted Meta + Google campaigns, and shipped a CRO program that lifted booked appointments 3.1x in 6 months.',
    tags: ['Google Ads', 'Meta Ads', 'CRO'],
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80'
  },
  {
    slug: 'seo-llm-visibility',
    industry: 'SaaS',
    title: 'SEO & LLM Visibility Growth',
    metric: '4.1x organic traffic + 220 AI citations',
    summary: 'Merged entity-based SEO with LLMO tactics. Result: dominant SERP visibility and consistent citations across ChatGPT, Claude, Perplexity and Gemini.',
    tags: ['SEO', 'LLMO', 'Content'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80'
  },
];

export const TESTIMONIALS = [
  { name: 'Aditi Sharma', role: 'VP Growth, Nova SaaS', quote: 'Ransen rebuilt our attribution in 3 weeks. We finally trust our data.' },
  { name: 'James O\u2019Brien', role: 'CMO, Harbor Homes', quote: 'They ship faster than any agency we\u2019ve worked with. Numbers speak for themselves.' },
  { name: 'Layla Al Farsi', role: 'Founder, Meridian DTC', quote: 'Creative velocity + clean signal = actual scale. Ransen delivered both.' },
];

export const RESOURCES = [
  { title: 'The 2025 State of LLM Optimization', category: 'Report', read: '18 min', tag: 'LLMO' },
  { title: 'Server-Side Tracking on Google Cloud: A Playbook', category: 'Guide', read: '22 min', tag: 'Analytics' },
  { title: 'Meta Advantage+ vs. Manual Campaigns in 2025', category: 'Analysis', read: '9 min', tag: 'Meta Ads' },
  { title: 'Building a CRO Program That Compounds', category: 'Playbook', read: '14 min', tag: 'CRO' },
  { title: 'Programmatic SEO: When It Works, When It Fails', category: 'Guide', read: '16 min', tag: 'SEO' },
  { title: 'AI Agents for Marketing Operations', category: 'Deep Dive', read: '20 min', tag: 'AI' },
];
